import { Metrics } from '../types/Lead';

export interface ScoringWeights {
  aiMlHiring: number;
  aiBudget: number;
  aiMlProductPotential: number;
  eventAttendance: number;
  aiMlRelatedProjects: number;
  organizationalMoves: number;
  overallScore: number;
}

export interface ScoringConfig {
  weights: ScoringWeights;
  version: string;
  lastUpdated: Date;
}

export const DEFAULT_SCORING_WEIGHTS: ScoringWeights = {
  aiMlHiring: 0.1,           // 10%
  aiBudget: 0.1,              // 10%
  aiMlProductPotential: 0.1, // 10%
  eventAttendance: 0.1,       // 10%
  aiMlRelatedProjects: 0.1, // 10%
  organizationalMoves: 0.1,   // 10%
  overallScore: 0.4,          // 40%
};

export const METRIC_LABELS: Record<keyof ScoringWeights, string> = {
  aiMlHiring: 'AI/ML Hiring',
  aiBudget: 'AI Budget',
  aiMlProductPotential: 'AI/ML Product Potential',
  eventAttendance: 'Event Attendance',
  aiMlRelatedProjects: 'AI/ML Projects',
  organizationalMoves: 'Organizational Moves',
  overallScore: 'Overall Score',
};

export const METRIC_DESCRIPTIONS: Record<keyof ScoringWeights, string> = {
  aiMlHiring: 'Weight for AI/ML hiring activity',
  aiBudget: 'Weight for AI budget allocation',
  aiMlProductPotential: 'Weight for AI/ML product potential',
  eventAttendance: 'Weight for AI/ML event attendance',
  aiMlRelatedProjects: 'Weight for AI/ML related projects',
  organizationalMoves: 'Weight for organizational restructuring',
  overallScore: 'Weight for the original overall assessment',
};

/**
 * Calculates a weighted average score for a lead
 */
export const calculateWeightedScore = (
  metrics: Metrics,
  overallScore: number,
  weights: ScoringWeights
): number => {
  // Validate inputs
  if (!metrics || !weights || typeof overallScore !== 'number' || isNaN(overallScore)) {
    console.warn('Invalid inputs to calculateWeightedScore:', { metrics, overallScore, weights });
    return overallScore || 0;
  }



  // Calculate weighted sum of all metric scores
  const metricKeys = Object.keys(metrics) as (keyof Metrics)[];
  console.log('🎯 Scoring calculation:', {
    metricKeys,
    overallScore,
    weights: Object.keys(weights),
    metrics: Object.keys(metrics)
  });

  const metricsSum = metricKeys.reduce((sum, key) => {
    const weight = weights[key as keyof ScoringWeights];
    const metric = metrics[key];

    // Skip if weight is undefined or metric is invalid
    if (weight === undefined || !metric || typeof metric.score !== 'number' || isNaN(metric.score)) {
      console.warn(`Skipping invalid metric: ${key}`, { weight, metric });
      return sum;
    }

    const contribution = metric.score * weight;
    console.log(`  ${key}: score=${metric.score}, weight=${weight}, contribution=${contribution}`);
    return sum + contribution;
  }, 0);

  // Add overall score contribution
  const overallContribution = overallScore * weights.overallScore;
  const weightedSum = metricsSum + overallContribution;

  console.log(`🎯 Final calculation: metricsSum=${metricsSum}, overallContribution=${overallContribution}, weightedSum=${weightedSum}`);

  // Validate the result
  if (isNaN(weightedSum)) {
    console.error('Weighted sum calculation resulted in NaN:', { metricsSum, overallContribution, weights });
    return overallScore;
  }

  // Ensure the result is between 1 and 10 (same range as original scores)
  const result = Math.max(1, Math.min(10, weightedSum));

  // Log first few calculations or when score significantly differs from original
  if (Math.abs(result - overallScore) > 0.5) {
    console.log(`🎯 Scoring: ${overallScore} → ${result.toFixed(1)} (weights: ${JSON.stringify(weights).slice(0,50)}...)`);
  }

  return result;
};

/**
 * Normalizes weights to ensure they sum to 1.0
 */
export const normalizeWeights = (weights: ScoringWeights): ScoringWeights => {
  const sum = Object.values(weights).reduce((acc, weight) => acc + weight, 0);

  if (sum === 0) {
    return { ...DEFAULT_SCORING_WEIGHTS };
  }

  const normalized: ScoringWeights = {} as ScoringWeights;
  Object.entries(weights).forEach(([key, value]) => {
    normalized[key as keyof ScoringWeights] = value / sum;
  });

  return normalized;
};

/**
 * Validates that all weights are non-negative
 */
export const validateWeights = (weights: ScoringWeights): boolean => {
  return Object.values(weights).every(weight => weight >= 0);
};